/**
 * Shared helpers for the Gantt page charts (canvas charts + the DOM Gantt):
 * round-number axis ticks, number/time formatting and the chart palette.
 * The PDF report (backend/report_pdf.py) mirrors these colours.
 */

/** Chart palette. Series colours match the legends and the PDF. */
export const CHART = {
  grid:     "#d7e0ea",
  axis:     "#67788a",   // tick labels / axis titles
  ink:      "#2f4b66",   // value labels
  incoming: "#5e6e7d",
  consumed: "#1E3A8A",
  frozen:   "#0EA5E9",
  goal:     "#d94f4f",
  now:      "#1E3A8A",   // current flow / current crews
  peak:     "#047857",   // highest point on vs Flow, recommended crews
  flow:     "#0EA5E9",   // vs Flow curve + vs Crews bars
  barDim:   "#bcdff0",   // vs Crews bars short of the target
} as const;

/** A round tick step (1, 2 or 5 × 10^n) that splits `span` into at most `maxTicks` intervals. */
export function niceStep(span: number, maxTicks = 6): number {
  if (!(span > 0) || !isFinite(span)) return 1;
  const raw = span / Math.max(1, maxTicks);
  const mag = Math.pow(10, Math.floor(Math.log10(raw)));
  const norm = raw / mag;
  const m = norm <= 1 ? 1 : norm <= 2 ? 2 : norm <= 5 ? 5 : 10;
  return m * mag;
}

/**
 * Round-number ticks covering [lo, hi]. The axis runs from `min` (the first
 * tick, ≤ lo) to `max` (the last tick, ≥ hi); every tick is a multiple of `step`.
 */
export function niceTicks(lo: number, hi: number, maxTicks = 6) {
  if (!(hi > lo)) hi = lo + 1;
  const step = niceStep(hi - lo, maxTicks);
  const min = Math.floor(lo / step + 1e-9) * step;
  const max = Math.ceil(hi / step - 1e-9) * step;
  const ticks: number[] = [];
  for (let v = min; v <= max + step * 1e-6; v += step) ticks.push(+v.toFixed(10));
  return { min, max, step, ticks };
}

/** Decimals needed to print every multiple of `step` exactly (0 for whole steps). */
export const stepDecimals = (step: number) =>
  Math.max(0, -Math.floor(Math.log10(step) + 1e-9));

/** Whole number with thousands separators: 2600 → "2,600". */
export const fmtInt = (x: number) => Math.round(x).toLocaleString("en-GB");

/** Tonnes from kg, 1 dp: 35600 → "35.6 t". */
export const fmtT = (kg: number) => `${(kg / 1000).toFixed(1)} t`;

/** Hours, 1 dp; whole hours without the decimal: 11.6 → "11.6 h", 16 → "16 h". */
export const fmtH = (h: number) =>
  `${Math.abs(h - Math.round(h)) < 0.05 ? Math.round(h) : h.toFixed(1)} h`;

/** Minutes from the shift start as h:mm: 552 → "9:12". */
export function fmtHm(min: number): string {
  const m = Math.max(0, Math.round(min));
  return `${Math.floor(m / 60)}:${String(m % 60).padStart(2, "0")}`;
}

/** A duration in minutes: 8 → "8 min", 167 → "2 h 47 min", 120 → "2 h". */
export function fmtDur(min: number): string {
  const m = Math.max(0, Math.round(min));
  if (m < 60) return `${m} min`;
  const h = Math.floor(m / 60), r = m % 60;
  return r ? `${h} h ${r} min` : `${h} h`;
}
